import type {
  MSADataType,
  MSADecisionLineItemType,
  MSADecisionType,
  MSADevelopmentType,
  MSAReReviewType,
  PaginatedResponse,
  PrescriptionRow,
  TreatmentRow,
} from "types/DataTypes.ts";

import { api } from "./api.ts";

// MSA Decision Line Item types
type CreateMsaDecisionLineItemArgs = {
  data: unknown;
};

type PatchMsaDecisionLineItemArgs = {
  data: unknown;
  id: string | number;
};

type DeleteMsaDecisionLineItemArgs = {
  id: string | number;
};

// MSA types
type GetMSAsArgs = {
  type?: string;
  searchTerm?: string;
  limit?: number;
  offset?: number;
};

type GetMSAByIdArgs = {
  msaId: string | number;
};

type GetMSAByFileIdArgs = {
  fileId: string | number;
  includeValidations?: boolean;
};

type PatchMSAArgs = {
  msaId: string | number;
  data: Partial<MSADataType>;
};

type PatchMSATypeArgs = {
  msaId: string | number;
  data: unknown;
};

type ConvertFMServiceArgs = {
  serviceId: string | number;
  data: unknown;
  serviceType?: string;
};

// MSA Decision types
type GetMSADecisionsArgs = {
  msaId: string;
};

type GetMSADecisionsByFileArgs = {
  fileId: number;
};

type PatchMSADecisionArgs = {
  msaId: string;
  decisionId: string;
  // confirm_ocr_letter isn't a real MSADecision field; it tells the backend to also file the confirmed letter.
  data: Partial<MSADecisionType> & { confirm_ocr_letter?: boolean };
};

type CreateMSADecisionArgs = {
  msaId: string;
};

type DeleteMSADecisionArgs = {
  decisionId: string;
  confirmed: boolean;
};

type UploadOCRMSADecisionResponse = {
  filename: string;
  job_id: number;
  // No textract_request_id, since submitting only creates the BackgroundJob; the TextractRequest comes later.
  message: string;
};

type SubmitOCRMSADecisionArgs = {
  msaDecisionId: string | number;
  documentId: number;
};

type SubmitOCRMSADevelopmentArgs = {
  msaId: string | number;
  documentId: number;
};

// MSA Development types
type CreateCMSDevelopmentArgs = {
  msaId: string;
};

type PatchCMSDevelopmentArgs = {
  developmentId: string;
  data: Partial<MSADevelopmentType>;
};

type DeleteCMSDevelopmentArgs = {
  developmentId: string;
};

// MSA Document Generation types
type GenerateMSADocumentArgs = {
  msaId: string | number;
  documentType: string;
  allocationFilter?: string;
};

type GenerateCostProjectionDocumentArgs = {
  costProjectionId: string | number;
  documentType: string;
  allocationFilter?: string;
};

type GenerateMSAAllocatorNotesArgs = {
  msaId: string | number;
  data: unknown;
};

type GenerateCostProjectionAllocatorNotesArgs = {
  costProjectionId: string | number;
  data: unknown;
};

// MSA Re-Review types
type CreateMSAReReviewArgs = {
  msaId: string;
  category: string;
};

type DeleteMSAReReviewArgs = {
  reviewId: string;
};

type GetMSAReReviewsArgs = {
  msaId: string;
};

type PatchMSAReReviewArgs = {
  reviewId: string;
  data: Partial<MSAReReviewType>;
};

type GetMSAAuditHistoryArgs = {
  id: string | number;
};

// Treatment Allocation types
type GetAllTreatmentAllocationsArgs = {
  params?: Record<string, unknown>;
};

type GetTreatmentAllocationArgs = {
  filters?: Record<string, unknown>;
};

type CreateTreatmentAllocationArgs = {
  data: unknown;
};

type CloneTreatmentAllocationArgs = {
  id: string | number;
};

type PatchTreatmentAllocationArgs = {
  id: string | number;
  data: unknown;
};

type DeleteTreatmentAllocationArgs = {
  id: string | number;
  data: { msa?: string | number; cost_projection?: string | number };
};

type MoveTreatmentAllocationArgs = {
  id: string | number;
  data: unknown;
};

// Prescription Allocation types
type GetAllPrescriptionAllocationsArgs = {
  params?: Record<string, unknown>;
};

type GetPrescriptionAllocationArgs = {
  filters?: Record<string, unknown>;
};

type CreatePrescriptionAllocationArgs = {
  data: unknown;
};

type ClonePrescriptionAllocationArgs = {
  id: string | number;
};

type PatchPrescriptionAllocationArgs = {
  id: string | number;
  data: unknown;
};

type DeletePrescriptionAllocationArgs = {
  id: string | number;
  data: { msa?: string | number; cost_projection?: string | number };
};

type MovePrescriptionAllocationArgs = {
  id: string | number;
  data: unknown;
};

/// core MSA endpoints
export const msaAPI = api.injectEndpoints({
  endpoints: (build) => ({
    // Typed as MSADecisionLineItemType so callers can .unwrap() without a cast.
    createMsaDecisionLineItem: build.mutation<
      MSADecisionLineItemType,
      CreateMsaDecisionLineItemArgs
    >({
      query: ({ data }) => ({
        url: `api/v1/msa-decision-line-items/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["MSADecision"],
    }),
    patchMsaDecisionLineItemById: build.mutation<
      unknown,
      PatchMsaDecisionLineItemArgs
    >({
      query: ({ data, id }) => ({
        url: `api/v1/msa-decision-line-items/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    deleteMsaDecisionLineItemById: build.mutation<
      void,
      DeleteMsaDecisionLineItemArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/msa-decision-line-items/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["MSADecision"],
    }),
    getMSAs: build.query<PaginatedResponse<MSADataType>, GetMSAsArgs>({
      query: ({ type, searchTerm, limit, offset }) => {
        const params = new URLSearchParams({ ordering: "-created" });
        if (type) params.append("type", type);
        if (limit !== undefined) params.append("limit", limit.toString());
        if (offset !== undefined) params.append("offset", offset.toString());
        if (searchTerm) params.append("filter", searchTerm);

        return {
          url: `api/v1/msas/?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["MSATable"],
    }),
    getMSAById: build.query<MSADataType, GetMSAByIdArgs>({
      query: ({ msaId }) => ({
        url: `api/v1/msas/${msaId}/`,
        method: "GET",
      }),
      providesTags: ["MSA", "MSAValidation"],
    }),
    getMSAByFileId: build.query<
      PaginatedResponse<MSADataType>,
      GetMSAByFileIdArgs
    >({
      query: ({ fileId, includeValidations = true }) => ({
        url: `api/v1/msas/?file_id=${fileId}&include_validations=${includeValidations}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { fileId }) => [
        { type: "MSATable", id: `FILE-${fileId}` },
      ],
    }),
    getMSAValidationById: build.query<unknown, GetMSAByIdArgs>({
      query: ({ msaId }) => ({
        url: `api/v1/msas/${msaId}/validate/`,
        method: "GET",
      }),
      providesTags: ["MSAValidation"],
    }),
    patchMSAById: build.mutation<MSADataType, PatchMSAArgs>({
      query: ({ msaId, data }) => ({
        url: `api/v1/msas/${msaId}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async ({ msaId, data }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          msaAPI.util.updateQueryData(
            "getMSAById",
            { msaId: String(msaId) },
            (draft) => {
              Object.assign(draft, data);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["MSA", "MSAValidation", "RatedAge"],
    }),
    patchMSAType: build.mutation<unknown, PatchMSATypeArgs>({
      query: ({ msaId, data }) => ({
        url: `api/v1/msas/${msaId}/change-type/`,
        method: "PATCH",
        body: data,
      }),
      // don't invalidate tags - with ConvertServiceModal, we now refresh everytime
    }),
    convertFMService: build.mutation<unknown, ConvertFMServiceArgs>({
      query: ({ serviceId, data, serviceType }) => {
        // determine the endpoint based on current service type
        const isMSAType = [
          "Medicare Set-Aside",
          "MSA-111",
          "Non-Threshold MSA",
          "Pre-MSA Snapshot",
          "Amended Review",
        ].includes(serviceType || "");
        const endpoint = isMSAType
          ? `api/v1/msas/${serviceId}/convert/`
          : `api/v1/cost-projections/${serviceId}/convert/`;

        return {
          url: endpoint,
          method: "POST",
          body: data,
        };
      },
      // don't invalidate tags - the old service is deleted and we're navigating away
      // the new page will fetch fresh data for the new service
    }),
    getMSADecisions: build.query<MSADecisionType[], GetMSADecisionsArgs>({
      query: ({ msaId }) => ({
        url: `/api/v1/msa-decisions/?msa=${msaId}`,
        method: "GET",
      }),
      providesTags: ["MSADecision"],
    }),
    // Every decision across every MSA on a file, not just one MSA.
    getMSADecisionsByFile: build.query<
      MSADecisionType[],
      GetMSADecisionsByFileArgs
    >({
      query: ({ fileId }) => ({
        url: `/api/v1/msa-decisions/?msa__file=${fileId}`,
        method: "GET",
      }),
      providesTags: ["MSADecision"],
    }),
    patchMSADecision: build.mutation<MSADecisionType, PatchMSADecisionArgs>({
      query: ({ decisionId, data }) => ({
        url: `/api/v1/msa-decisions/${decisionId}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["MSA", "MSADecision"],
    }),
    createMSADecision: build.mutation<MSADecisionType, CreateMSADecisionArgs>({
      query: ({ msaId }) => ({
        url: `/api/v1/msa-decisions/`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: { msa: msaId },
      }),
      invalidatesTags: ["MSA", "MSADecision", "MSAReReview"],
    }),
    deleteMSADecision: build.mutation<unknown, DeleteMSADecisionArgs>({
      query: ({ decisionId }) => ({
        url: `api/v1/msa-decisions/${decisionId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["MSA", "MSADecision", "MSAReReview"],
    }),
    submitOCRMSADecision: build.mutation<
      UploadOCRMSADecisionResponse,
      SubmitOCRMSADecisionArgs
    >({
      query: ({ msaDecisionId, documentId }) => ({
        url: "api/v1/ocr-msa-decision/submit/",
        method: "POST",
        body: { msa_decision_id: msaDecisionId, document_id: documentId },
      }),
      // Also invalidates TextractRequest so OCRStatusList refetches right away and shows the new job.
      invalidatesTags: ["BackgroundJobs", "TextractRequest"],
    }),
    submitOCRMSADevelopment: build.mutation<
      UploadOCRMSADecisionResponse,
      SubmitOCRMSADevelopmentArgs
    >({
      query: ({ msaId, documentId }) => ({
        url: "api/v1/ocr-msa-development/submit/",
        method: "POST",
        body: { msa_id: msaId, document_id: documentId },
      }),
      invalidatesTags: ["BackgroundJobs", "TextractRequest"],
    }),
    createCMSDevelopment: build.mutation<
      MSADevelopmentType,
      CreateCMSDevelopmentArgs
    >({
      query: ({ msaId }) => ({
        url: `/api/v1/msa-developments/`,
        method: "POST",
        body: { msa: msaId },
      }),
      invalidatesTags: ["MSA"],
    }),
    patchCMSDevelopment: build.mutation<
      MSADevelopmentType,
      PatchCMSDevelopmentArgs
    >({
      query: ({ developmentId, data }) => ({
        url: `/api/v1/msa-developments/${developmentId}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["MSA"],
    }),
    deleteCMSDevelopment: build.mutation<unknown, DeleteCMSDevelopmentArgs>({
      query: ({ developmentId }) => ({
        url: `/api/v1/msa-developments/${developmentId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["MSA"],
    }),
    generateMSADocument: build.mutation<unknown, GenerateMSADocumentArgs>({
      query: ({ msaId, documentType, allocationFilter }) => {
        const body: { document_type: string; allocation_filter?: string } = {
          document_type: documentType,
        };
        if (allocationFilter) {
          body.allocation_filter = allocationFilter;
        }
        return {
          url: `api/v1/msas/${msaId}/generate-document/`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Documents"],
    }),
    generateCostProjectionDocument: build.mutation<
      unknown,
      GenerateCostProjectionDocumentArgs
    >({
      query: ({ costProjectionId, documentType, allocationFilter }) => {
        const body: { document_type: string; allocation_filter?: string } = {
          document_type: documentType,
        };
        if (allocationFilter) {
          body.allocation_filter = allocationFilter;
        }
        return {
          url: `api/v1/cost-projections/${costProjectionId}/generate-document/`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Documents"],
    }),
    generateMSAAllocatorNotes: build.mutation<
      unknown,
      GenerateMSAAllocatorNotesArgs
    >({
      query: ({ msaId, data }) => ({
        url: `api/v1/msas/${msaId}/generate-allocator-notes/`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: data,
      }),
      invalidatesTags: ["Documents"],
    }),
    generateCostProjectionAllocatorNotes: build.mutation<
      unknown,
      GenerateCostProjectionAllocatorNotesArgs
    >({
      query: ({ costProjectionId, data }) => ({
        url: `api/v1/cost-projections/${costProjectionId}/generate-allocator-notes/`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: data,
      }),
      invalidatesTags: ["Documents"],
    }),
    createMSAReReview: build.mutation<unknown, CreateMSAReReviewArgs>({
      query: ({ msaId, category }) => {
        return {
          url: `api/v1/msa-re-reviews/`,
          method: "POST",
          body: {
            msa: msaId,
            category: category,
          },
        };
      },
      invalidatesTags: ["MSA", "MSAReReview", "MSADecision"],
    }),
    deleteMSAReReview: build.mutation<unknown, DeleteMSAReReviewArgs>({
      query: ({ reviewId }) => ({
        url: `api/v1/msa-re-reviews/${reviewId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["MSA", "MSAReReview", "MSADecision"],
    }),
    getMSAReReviews: build.query<MSAReReviewType[], GetMSAReReviewsArgs>({
      query: ({ msaId }) => ({
        url: `/api/v1/msa-re-reviews/?msa=${msaId}`,
        method: "GET",
      }),
      providesTags: ["MSAReReview"],
    }),
    patchMSAReReview: build.mutation<MSAReReviewType, PatchMSAReReviewArgs>({
      query: ({ reviewId, data }) => ({
        url: `/api/v1/msa-re-reviews/${reviewId}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["MSA", "MSAReReview"],
    }),
    getMSAAuditHistory: build.query<unknown, GetMSAAuditHistoryArgs>({
      query: ({ id }) => ({
        url: `api/v1/msas/${id}/audit_trail/`,
        method: "GET",
      }),
      providesTags: ["MSA", "CmsSubmissionPackages"],
    }),
  }),
  overrideExisting: false,
});

// treatment allocation endpoints
export const treatmentAllocationAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getAllTreatmentAllocations: build.query<
      unknown,
      GetAllTreatmentAllocationsArgs
    >({
      query: ({ params }) => ({
        url: `api/v1/treatment-allocations/`,
        method: "GET",
        params,
      }),
      providesTags: ["ClientSubmission"],
    }),
    getTreatmentAllocation: build.query<
      PaginatedResponse<TreatmentRow>,
      GetTreatmentAllocationArgs
    >({
      query: ({ filters }) => {
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              params.append(key, String(value));
            }
          });
        }
        return {
          url: `api/v1/treatment-allocations/?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["TreatmentAllocation", "PrescriptionAllocation"],
    }),
    createTreatmentAllocation: build.mutation<
      unknown,
      CreateTreatmentAllocationArgs
    >({
      query: ({ data }) => ({
        url: `api/v1/treatment-allocations/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "TreatmentAllocation",
        "PrescriptionAllocation",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
      ],
    }),
    cloneTreatmentAllocation: build.mutation<
      unknown,
      CloneTreatmentAllocationArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/treatment-allocations/${id}/clone/`,
        method: "POST",
      }),
      invalidatesTags: [
        "TreatmentAllocation",
        "PrescriptionAllocation",
        "ClientSubmission",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
        "SurgicalPricingCalculation",
      ],
    }),
    patchTreatmentAllocationById: build.mutation<
      unknown,
      PatchTreatmentAllocationArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/treatment-allocations/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "TreatmentAllocation",
        "PrescriptionAllocation",
        "ClientSubmission",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
        "SurgicalPricingCalculation",
      ],
    }),
    deleteTreatmentAllocationById: build.mutation<
      unknown,
      DeleteTreatmentAllocationArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/treatment-allocations/${id}/`,
        method: "DELETE",
        body: data,
      }),
      onQueryStarted: async ({ id, data }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          treatmentAllocationAPI.util.updateQueryData(
            "getTreatmentAllocation",
            { filters: { msa_id: data.msa } },
            (draft) => {
              if (draft && draft.results) {
                const index = draft.results.findIndex(
                  (item: { id: number | string }) => item.id === id,
                );
                if (index !== -1) {
                  draft.results.splice(index, 1);
                  // update count if it exists
                  if (draft.count !== undefined) {
                    draft.count = Math.max(0, draft.count - 1);
                  }
                }
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
      invalidatesTags: [
        "TreatmentAllocation",
        "PrescriptionAllocation",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
      ],
    }),
    moveTreatmentAllocation: build.mutation<
      unknown,
      MoveTreatmentAllocationArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/treatment-allocations/${id}/move/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "TreatmentAllocation",
        "PrescriptionAllocation",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
      ],
    }),
  }),
  overrideExisting: false,
});

// prescription allocation endpoints
export const prescriptionAllocationAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getAllPrescriptionAllocations: build.query<
      unknown,
      GetAllPrescriptionAllocationsArgs
    >({
      query: ({ params }) => ({
        url: `api/v1/prescription-allocations/`,
        method: "GET",
        params,
      }),
      providesTags: ["ClientSubmission"],
    }),
    getPrescriptionAllocation: build.query<
      PaginatedResponse<PrescriptionRow>,
      GetPrescriptionAllocationArgs
    >({
      query: ({ filters }) => {
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              params.append(key, String(value));
            }
          });
        }
        return {
          url: `api/v1/prescription-allocations/?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["PrescriptionAllocation", "TreatmentAllocation"],
    }),
    createPrescriptionAllocation: build.mutation<
      unknown,
      CreatePrescriptionAllocationArgs
    >({
      query: ({ data }) => ({
        url: `api/v1/prescription-allocations/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "PrescriptionAllocation",
        "TreatmentAllocation",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
      ],
    }),
    clonePrescriptionAllocation: build.mutation<
      unknown,
      ClonePrescriptionAllocationArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/prescription-allocations/${id}/clone/`,
        method: "POST",
      }),
      invalidatesTags: [
        "PrescriptionAllocation",
        "TreatmentAllocation",
        "ClientSubmission",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
      ],
    }),
    patchPrescriptionAllocationById: build.mutation<
      unknown,
      PatchPrescriptionAllocationArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/prescription-allocations/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "PrescriptionAllocation",
        "TreatmentAllocation",
        "ClientSubmission",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
      ],
    }),
    deletePrescriptionAllocationById: build.mutation<
      unknown,
      DeletePrescriptionAllocationArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/prescription-allocations/${id}/`,
        method: "DELETE",
        body: data,
      }),
      onQueryStarted: async ({ id, data }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          prescriptionAllocationAPI.util.updateQueryData(
            "getPrescriptionAllocation",
            { filters: { msa_id: data.msa } },
            (draft) => {
              if (draft && draft.results) {
                const index = draft.results.findIndex(
                  (item: { id: number | string }) => item.id === id,
                );
                if (index !== -1) {
                  draft.results.splice(index, 1);
                  // update count if it exists
                  if (draft.count !== undefined) {
                    draft.count = Math.max(0, draft.count - 1);
                  }
                }
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [
        "PrescriptionAllocation",
        "TreatmentAllocation",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
      ],
    }),
    movePrescriptionAllocation: build.mutation<
      unknown,
      MovePrescriptionAllocationArgs
    >({
      query: ({ id, data }) => ({
        url: `api/v1/prescription-allocations/${id}/move/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "PrescriptionAllocation",
        "TreatmentAllocation",
        "MSA",
        "MCP",
        "LCP",
        "MSAValidation",
        "CostProjectionValidation",
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMSAsQuery,
  useGetMSAByIdQuery,
  useGetMSAByFileIdQuery,
  useGetMSAValidationByIdQuery,
  usePatchMSAByIdMutation,
  usePatchCMSDevelopmentMutation,
  useDeleteCMSDevelopmentMutation,
  useGenerateMSADocumentMutation,
  useGenerateCostProjectionDocumentMutation,
  useGenerateMSAAllocatorNotesMutation,
  useGenerateCostProjectionAllocatorNotesMutation,
  useCreateCMSDevelopmentMutation,
  useCreateMSADecisionMutation,
  useGetMSADecisionsQuery,
  useGetMSADecisionsByFileQuery,
  usePatchMSADecisionMutation,
  useCreateMSAReReviewMutation,
  useGetMSAReReviewsQuery,
  usePatchMSAReReviewMutation,
  useCreateMsaDecisionLineItemMutation,
  usePatchMsaDecisionLineItemByIdMutation,
  useDeleteMsaDecisionLineItemByIdMutation,
  useDeleteMSADecisionMutation,
  useSubmitOCRMSADecisionMutation,
  useSubmitOCRMSADevelopmentMutation,
  useDeleteMSAReReviewMutation,
  usePatchMSATypeMutation,
  useConvertFMServiceMutation,
  useGetMSAAuditHistoryQuery,
} = msaAPI;

export const {
  useGetAllTreatmentAllocationsQuery,
  useGetTreatmentAllocationQuery,
  useCreateTreatmentAllocationMutation,
  useCloneTreatmentAllocationMutation,
  usePatchTreatmentAllocationByIdMutation,
  useDeleteTreatmentAllocationByIdMutation,
  useMoveTreatmentAllocationMutation,
} = treatmentAllocationAPI;

export const {
  useGetAllPrescriptionAllocationsQuery,
  useGetPrescriptionAllocationQuery,
  useCreatePrescriptionAllocationMutation,
  useClonePrescriptionAllocationMutation,
  usePatchPrescriptionAllocationByIdMutation,
  useDeletePrescriptionAllocationByIdMutation,
  useMovePrescriptionAllocationMutation,
} = prescriptionAllocationAPI;
